import { computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { useCompanyStore } from '@/stores/company'

/**
 * Composable para controlar la visibilidad de módulos del sistema
 * basado en flags de la compañía en Firestore (documento Companies/{companyId}).
 *
 * Flags soportados:
 * - showEmail: controla visibilidad del módulo Buzón
 * - showPurchaseOrder: controla visibilidad del módulo Órdenes de Compra
 * - showQuotes: controla visibilidad del módulo Cotizaciones
 * - showDocGenAI: controla visibilidad del módulo DocGen AI
 * - showAiAgent: controla visibilidad del módulo Asistente AI
 * - showDashboard: controla visibilidad del módulo Dashboards
 * - showReports: controla visibilidad del módulo Reportes
 * - showControllTower: controla visibilidad del acceso directo Torre de Control de Riesgo
 * - showAuditReport: controla visibilidad del acceso directo Auditoría de Órdenes
 * - showContainerReport: controla visibilidad del acceso directo Control de Contenedores
 *
 * Los tres reportes predefinidos aceptan además una lista de uids autorizados
 * (controlTowerAllowedUids, auditReportAllowedUids, containerReportAllowedUids):
 * si trae uids, solo esos usuarios ven la opción; vacía o ausente = toda la
 * compañía. Ver REPORTES_PREDEFINIDOS.
 * - showSharePointFolders: controla visibilidad del botón para importar archivos
 *   desde OneDrive/SharePoint en los documentos de embarques y órdenes de compra
 *
 * Si el flag no existe en el documento, el módulo se oculta por defecto.
 */
/**
 * Reportes predefinidos con acceso directo en el menú. Cada uno se controla con
 * dos campos de `Companies/{companyId}`: el flag que lo enciende para la
 * compañía y, opcionalmente, la lista de uids que pueden verlo dentro de ella.
 *
 * El id del módulo coincide con la ruta (/dashboard/<módulo>) y con el slug del
 * reporte en el backend, así que el guard de la ruta usa esta misma regla.
 */
export const REPORTES_PREDEFINIDOS = {
  'control-tower': { flag: 'showControllTower', uids: 'controlTowerAllowedUids' },
  'order-audit': { flag: 'showAuditReport', uids: 'auditReportAllowedUids' },
  'container-control': { flag: 'showContainerReport', uids: 'containerReportAllowedUids' }
} as const

export type ReportePredefinido = keyof typeof REPORTES_PREDEFINIDOS

export function useModuleVisibility() {
  const authStore = useAuthStore()
  const companyStore = useCompanyStore()

  /**
   * Visibilidad de un reporte predefinido para el usuario de la sesión: el flag
   * de la compañía manda, y la lista de uids restringe dentro de ella. Lista
   * ausente o vacía = todos; con uids = solo esos. Un valor que no sea un
   * arreglo se ignora (se trata como "sin restricción"), porque un campo mal
   * escrito en Firestore no debe dejar a nadie sin el reporte.
   */
  const isReportVisible = (report: ReportePredefinido): boolean => {
    const company = companyStore.currentCompanyData
    const { flag, uids } = REPORTES_PREDEFINIDOS[report]
    if (company?.[flag] !== true) return false

    const allowed = company[uids]
    if (!Array.isArray(allowed) || allowed.length === 0) return true

    const uid = authStore.user?.uid
    return typeof uid === 'string' && uid.length > 0 && allowed.includes(uid)
  }

  const showEmail = computed(() => {
    return companyStore.currentCompanyData?.showEmail === true
  })

  const showPurchaseOrder = computed(() => {
    return companyStore.currentCompanyData?.showPurchaseOrder === true
  })

  const showMetrics = computed(() => {
    return companyStore.currentCompanyData?.showMetrics === true
  })

  const showQuotes = computed(() => {
    return companyStore.currentCompanyData?.showQuotes === true
  })

  const showDocGenAI = computed(() => {
    return companyStore.currentCompanyData?.showDocGenAI === true
  })

  const showAiAgent = computed(() => {
    return companyStore.currentCompanyData?.showAiAgent === true
  })

  const showDashboard = computed(() => {
    return companyStore.currentCompanyData?.showDashboard === true
  })

  const showReports = computed(() => {
    return companyStore.currentCompanyData?.showReports === true
  })

  const showWarehouse = computed(() => {
    return companyStore.currentCompanyData?.showWarehouse === true
  })

  // Accesos directos por cliente a los reportes predefinidos, en el menú de
  // Operaciones. Independientes de showAiAgent y restringibles por uid.
  const showControllTower = computed(() => isReportVisible('control-tower'))

  const showAuditReport = computed(() => isReportVisible('order-audit'))

  const showContainerReport = computed(() => isReportVisible('container-control'))

  // Importador de archivos de OneDrive/SharePoint. El modo de conexión (OneDrive
  // personal de cada usuario o una cuenta compartida de la compañía) lo resuelve
  // el backend desde Companies/{companyId}/config/SHAREPOINT; este flag solo
  // controla si el botón se ve.
  const showSharePointFolders = computed(() => {
    return companyStore.currentCompanyData?.showSharePointFolders === true
  })

  /**
   * Determina si un módulo de navegación debe ser visible.
   * Los módulos no controlados por flags siempre son visibles.
   */
  const isModuleVisible = (moduleId: string): boolean => {
    switch (moduleId) {
      case 'emails':
        return showEmail.value
      case 'purchase-orders':
        return showPurchaseOrder.value
      case 'usage-analytics':
        return showMetrics.value
      case 'quotes':
        return showQuotes.value
      case 'docgen':
        return showDocGenAI.value
      case 'assistant':
        return showAiAgent.value
      case 'dashboards':
        return showDashboard.value
      case 'reports':
        return showReports.value
      case 'warehouse':
        return showWarehouse.value
      case 'control-tower':
        return showControllTower.value
      case 'order-audit':
        return showAuditReport.value
      case 'container-control':
        return showContainerReport.value
      default:
        return true
    }
  }

  return {
    showEmail,
    showPurchaseOrder,
    showMetrics,
    showQuotes,
    showDocGenAI,
    showAiAgent,
    showDashboard,
    showReports,
    showWarehouse,
    showControllTower,
    showAuditReport,
    showContainerReport,
    showSharePointFolders,
    isModuleVisible,
    isReportVisible
  }
}
